import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info, Loader2, ExternalLink, Download, Flame, FileText, Mail, ChevronDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Trattativa } from "./VestaTrattativa";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { eur } from "@/lib/price";
import { useVestaView, voiceStore } from "./vestaStore";
import { MailComposer } from "./VestaMail";

const LABELS = {
  potenza_kwp: "Potenza (kWp)", n_pannelli: "Pannelli", accumulo_kwh: "Accumulo (kWh)", consumo_annuo_kwh: "Consumo annuo (kWh)", superficie_mq: "Superficie (m²)",
  roof_type: "Tetto", pbt: "Rientro investimento", n_binari_default: "Binari (default)", scarto_eur: "Scarto", prezzo_imponibile: "Imponibile", iva_importo: "IVA", prezzo_iva_incluso: "Prezzo IVA inclusa", totale_costi: "Totale costi", margine_eur: "Margine",
  proposta_generata: "Proposta generata", proposta_inviata_at: "Proposta inviata", proposta_inviata_a: "Inviata a", proposta_aperta_at: "Proposta aperta", proposta_aperture: "Aperture",
  contratto_firmato_at: "Contratto firmato", caparra_pagata_at: "Caparra pagata", caparra_importo: "Caparra", giorni_dilazione: "Giorni dilazione", tipo_copertura: "Copertura",
};
const MONEY = /^prezzo(?!_energia)|iva_importo|costi|margine|caparra_importo|importo|_eur$/;
const SKIP = new Set(["fornitura", "prezzi_riservati", "preventivo_attivo", "documenti", "documenti_disponibili", "matrice", "self_token", "lead_id", "id", "falda_id", "public_token", "overlay_image_url", "immagine_url", "immagini_caricate", "centro", "coordinate", "mese", "module_listino_id", "installer_id", "config_id", "installatore", "installatore_sede", "bar_len"]);
const UNITS = [[/ eur kwh\b/g, " (€/kWh)"], [/ kwh kwp\b/g, " (kWh/kWp)"], [/ kwh\b/g, " (kWh)"], [/ kwp\b/g, " (kWp)"], [/ m2\b/g, " (m²)"], [/ deg\b/g, " (°)"], [/ eur\b/g, " (€)"], [/ pct\b/g, " (%)"], [/ kw\b/g, " (kW)"], [/ wp\b/g, " (Wp)"], [/ mm\b/g, " (mm)"], [/\bpvgis\b/g, "PVGIS"], [/\bf([123])\b/g, "F$1"]];
const human = (k) => LABELS[k] || UNITS.reduce((s, [re, r]) => s.replace(re, r), ` ${k.replace(/_/g, " ")}`).trim().replace(/^./, (c) => c.toUpperCase());
const monthly = (v) => Array.isArray(v) && v.length === 12 && v.every((x) => typeof x === "number" || (x && typeof x === "object" && "mese" in x));
const isDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v);
const fmt = (k, v) => {
  if (v === true) return "Sì";
  if (v === false) return "No";
  if (isDate(v)) return new Date(v).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  if (typeof v === "number") return MONEY.test(k) ? eur(v) : v.toLocaleString("it-IT");
  return String(v);
};

// Righe chiave/valore da un oggetto annidato (salta matrici e array di oggetti complessi)
const rows = (o, pre = "") => Object.entries(o || {}).flatMap(([k, v]) => {
  if (SKIP.has(k) || v == null || v === "") return [];
  if (monthly(v)) return [];
  if (Array.isArray(v)) {
    if (!v.length) return [];
    if (v.every((x) => typeof x !== "object")) return [[pre + human(k), v.join(", ")]];
    return v.flatMap((x, i) => (x && typeof x === "object" ? rows(x, `${pre}${human(k).replace(/e$/, "a")} ${i + 1} · `) : []));
  }
  if (typeof v === "object") return rows(v, `${pre}${human(k)} · `);
  return [[pre + human(k), fmt(k, v)]];
});

function Section({ title, data, testid }) {
  const r = rows(data);
  if (!r.length) return null;
  return (
    <section data-testid={testid}>
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#C17F59]">{title}</div>
      <div className="divide-y divide-neutral-900 rounded-xl border border-neutral-800">
        {r.map(([k, v], i) => <div key={i} className="flex gap-3 px-3 py-1.5 text-xs"><span className="w-2/5 shrink-0 text-neutral-500">{k}</span><span className="min-w-0 flex-1 break-words text-neutral-200">{/^https?:\/\//.test(v) ? <a href={v} target="_blank" rel="noreferrer" className="text-[#E8B48F] underline">Apri link</a> : v}</span></div>)}
      </div>
    </section>
  );
}

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const series = (o, pre = "") => Object.entries(o || {}).flatMap(([k, v]) => {
  if (monthly(v) && v.every((x) => typeof x === "number")) return [[pre + human(k), v]];
  if (monthly(v)) {
    const keys = Object.keys(v[0]).filter((x) => x !== "mese" && typeof v[0][x] === "number");
    return keys.map((x) => [keys.length === 1 ? pre + human(k) : `${human(x)}`, [...v].sort((a, b) => a.mese - b.mese).map((m) => m[x] || 0)]);
  }
  return v && typeof v === "object" && !Array.isArray(v) ? series(v, `${pre}${human(k)} · `) : [];
});
const pick = (s) => {
  const seen = new Set();
  const rank = (n) => (/produz/i.test(n) ? 0 : /consumi/i.test(n) ? 1 : /eccedenz/i.test(n) ? 2 : 3);
  return s.filter(([, v]) => { const h = v.join(); if (seen.has(h)) return false; seen.add(h); return true; }).sort((a, b) => rank(a[0]) - rank(b[0])).slice(0, 3);
};

function Monthly({ data }) {
  const s = pick(series(data));
  if (!s.length) return null;
  const top = Math.max(1, ...s.flatMap(([, v]) => v));
  const col = ["#C17F59", "#E8E8E8", "#6b7280"];
  return (
    <div className="rounded-xl border border-neutral-800 p-3" data-testid="pratica-info-analisi-chart">
      <div className="mb-2 flex flex-wrap gap-3 text-[10px] text-neutral-400">{s.slice(0, 3).map(([k], i) => <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: col[i] }} />{k}</span>)}</div>
      <div className="flex h-32 items-end gap-1">
        {MONTHS.map((m, j) => (
          <div key={m} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex h-28 w-full items-end justify-center gap-px">{s.slice(0, 3).map(([k, v], i) => <div key={k} title={`${k} ${m}: ${v[j].toLocaleString("it-IT")}`} className="w-full max-w-[10px] rounded-t-sm" style={{ height: `${(v[j] / top) * 100}%`, background: col[i] }} />)}</div>
            <span className="text-[9px] text-neutral-500">{m}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const getDoc = async (id, doc) => {
  const t = toast.loading("Sto generando il documento…");
  try {
    const r = await api.get(`/rete/configs/${id}/documenti/${doc}`, { responseType: "blob", timeout: 95000 });
    toast.dismiss(t);
    return URL.createObjectURL(r.data);
  } catch (e) { toast.error("Documento non disponibile, riprova", { id: t }); return null; }
};

function Layout({ id, has }) {
  const [img, setImg] = useState(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    if (!has) return;
    api.get(`/rete/configs/${id}/documenti/layout.png`, { responseType: "blob", timeout: 95000 }).then((r) => setImg(URL.createObjectURL(r.data))).catch(() => {});
  }, [id, has]);
  if (!has) return null;
  return (
    <section data-testid="pratica-info-layout">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#C17F59]">Layout del tetto</div>
      {img ? <button type="button" onClick={() => setBig(!big)} className="block w-full overflow-hidden rounded-xl border border-neutral-800 bg-white" data-testid="pratica-info-layout-img">
        <img src={img} alt="Layout del tetto" className={`mx-auto w-full object-contain transition-[max-height] duration-300 ${big ? "max-h-[75vh]" : "max-h-56"}`} />
      </button> : <div className="flex h-24 items-center justify-center gap-2 rounded-xl border border-neutral-800 text-xs text-neutral-500"><Loader2 className="h-4 w-4 animate-spin text-[#C17F59]" />Sto generando il layout…</div>}
    </section>
  );
}

const btn = "inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]";

const STATO = { verificato: ["Verificato", "border-green-500/50 bg-green-500/10 text-green-300"], probabile: ["Probabile", "border-yellow-500/50 bg-yellow-500/10 text-yellow-300"],
  da_verificare: ["Da verificare", "border-orange-500/50 bg-orange-500/10 text-orange-300"], confermato_manuale: ["Confermato a mano", "border-sky-500/50 bg-sky-500/10 text-sky-300"] };
const statoKey = (s) => { const k = String(s || "").toLowerCase().replace(/[\s-]+/g, "_"); return k.startsWith("conferm") ? "confermato_manuale" : k; };

function Ente({ label, x, testid }) {
  if (!x || typeof x !== "object") return null;
  const nome = x.nome_verificato || x.nome || x.verificato;
  if (!nome && !x.stato) return null;
  const letto = x.nome_bolletta || x.nome_letto || x.letto_in_bolletta || x.bolletta;
  const st = STATO[statoKey(x.stato)];
  const motivi = Array.isArray(x.motivi) ? x.motivi : x.motivi ? [x.motivi] : [];
  const pod = x.pod || x.codice_pod;
  return (
    <div className="rounded-xl border border-neutral-800 p-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
        <span className="text-sm font-semibold text-white" data-testid={`${testid}-nome`}>{nome || "—"}</span>
        {x.stato && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st ? st[1] : "border-neutral-700 text-neutral-300"}`} data-testid={`${testid}-stato`}>{st ? st[0] : x.stato}</span>}
      </div>
      {letto && <div className="mt-1 text-xs text-neutral-400">Letto in bolletta: <span className="text-neutral-200">{letto}</span></div>}
      {pod && <div className="mt-1 text-xs text-neutral-400">Codice POD: <span className="font-mono text-neutral-200" data-testid={`${testid}-pod`}>{pod}</span></div>}
      {!!motivi.length && <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[11px] text-neutral-400">{motivi.map((m, i) => <li key={i}>{typeof m === "string" ? m : JSON.stringify(m)}</li>)}</ul>}
    </div>
  );
}

function Fornitura({ f }) {
  if (!f || typeof f !== "object") return null;
  return (
    <section data-testid="pratica-info-fornitura">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#C17F59]">Fornitura</div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Ente label="Fornitore" x={f.fornitore} testid="pratica-info-fornitore" />
        <Ente label="Distributore" x={f.distributore} testid="pratica-info-distributore" />
      </div>
    </section>
  );
}

const num = (v, u = "") => (v == null || v === "" ? null : typeof v === "number" ? `${v.toLocaleString("it-IT", { maximumFractionDigits: 2 })}${u}` : String(v));

function KeyNums({ d }) {
  const s = d.summary || {}, m = d.commerciale || {};
  const items = [["Potenza", num(m.potenza_kwp ?? s.potenza_kwp, " kWp")], ["Accumulo", num(m.accumulo_kwh ?? s.accumulo_kwh, " kWh")],
    ["Prezzo IVA incl.", m.prezzo_iva_incluso != null ? eur(m.prezzo_iva_incluso) : s.prezzo != null ? eur(s.prezzo) : null], ["Rientro", s.pbt], ["Risparmio", s.risparmio_annuo]].filter(([, v]) => v);
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="pratica-info-keynums">
      {items.map(([l, v]) => <div key={l} className="rounded-xl border border-neutral-800 bg-white/[0.03] px-3 py-2"><div className="text-[9px] uppercase tracking-widest text-neutral-500">{l}</div><div className="mt-0.5 font-mono text-sm font-bold text-white">{v}</div></div>)}
    </div>
  );
}

function Falde({ l }) {
  const f = (l?.falde || []).filter((x) => x && typeof x === "object");
  if (!f.length) return null;
  const c = "px-2 py-1.5 text-left";
  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-800" data-testid="pratica-info-falde">
      <table className="w-full text-xs"><thead className="text-[10px] uppercase tracking-wider text-neutral-500"><tr><th className={c}>Falda</th><th className={c}>Copertura</th><th className={c}>Inclin.</th><th className={c}>Azimut</th><th className={c}>Sup. netta</th><th className={c}>Esclusioni</th></tr></thead>
        <tbody className="divide-y divide-neutral-900 text-neutral-200">{f.map((x, i) => <tr key={i}><td className={c}>{i + 1}</td><td className={c}>{x.copertura || "—"}</td><td className={c}>{num(x.inclinazione_deg, "°") || "—"}</td><td className={c}>{num(x.azimut_deg, "°") || "—"}</td><td className={c}>{num(x.superficie_netta_m2, " m²") || "—"}</td><td className={c}>{x.esclusioni ?? "—"}</td></tr>)}</tbody></table>
    </div>
  );
}

function DocsMenu({ docs, openDoc, sendDoc, d }) {
  const has = (x) => docs.includes(x);
  if (!docs.length && !d.consulenza_url) return null;
  const it = "cursor-pointer gap-2 text-xs";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" className={btn} data-testid="pratica-info-docs-menu"><FileText className="h-3.5 w-3.5" />Documenti<ChevronDown className="h-3 w-3" /></button></DropdownMenuTrigger>
      <DropdownMenuContent className="z-[100] border-neutral-800 bg-neutral-950 text-neutral-200">
        {has("consulenza.pdf") && <DropdownMenuItem className={it} onSelect={() => openDoc("consulenza.pdf")} data-testid="pratica-info-doc-consulenza"><FileText className="h-3.5 w-3.5" />Consulenza PDF</DropdownMenuItem>}
        {has("proposta.pdf") && <DropdownMenuItem className={it} onSelect={() => openDoc("proposta.pdf")} data-testid="pratica-info-doc-proposta"><FileText className="h-3.5 w-3.5" />Proposta PDF</DropdownMenuItem>}
        {has("layout.png") && <DropdownMenuItem className={it} onSelect={() => openDoc("layout.png")} data-testid="pratica-info-doc-layout"><Download className="h-3.5 w-3.5" />Layout</DropdownMenuItem>}
        {(has("consulenza.pdf") || has("proposta.pdf")) && <DropdownMenuSeparator className="bg-neutral-800" />}
        {has("proposta.pdf") && <DropdownMenuItem className={it} onSelect={() => sendDoc("proposta.pdf")} data-testid="pratica-info-send-proposta"><Mail className="h-3.5 w-3.5" />Invia proposta</DropdownMenuItem>}
        {has("consulenza.pdf") && <DropdownMenuItem className={it} onSelect={() => sendDoc("consulenza.pdf")} data-testid="pratica-info-send-consulenza"><Mail className="h-3.5 w-3.5" />Invia consulenza</DropdownMenuItem>}
        {d.consulenza_url && <DropdownMenuItem className={it} onSelect={() => window.open(d.consulenza_url, "_blank", "noopener")} data-testid="pratica-info-consulenza"><ExternalLink className="h-3.5 w-3.5" />Link consulenza</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CfgView({ d, docs, openDoc, sendDoc, onOpen }) {
  const s = d.summary || {}, m = d.commerciale || {}, t = d.trattativa || {}, a = d.analisi_energetica, l = d.layout;
  const forn = d.fornitura || a?.fornitura;
  const tabs = [
    ["riepilogo", "Riepilogo", <Section key="r" title="In breve" testid="pratica-info-riepilogo" data={{ fornitore_attuale: t.fornitore_attuale || forn?.fornitore?.nome, pagamento: m.pagamento, wallbox: m.wallbox, manutenzione: m.manutenzione, copertura: s.copertura, consumo_annuo_kwh: s.consumo_annuo_kwh, margine_eur: m.margine_eur }} />],
    ["economia", "Economia", <Section key="e" title="Economia" testid="pratica-info-commerciale" data={{ prezzo_imponibile: m.prezzo_imponibile, iva_importo: m.iva_importo, prezzo_iva_incluso: m.prezzo_iva_incluso ?? s.prezzo, totale_costi: m.totale_costi, margine_eur: m.margine_eur, pagamento: m.pagamento, piano_di_pagamento: m.pagamento_piano, costo_attuale_energia: s.costo_corrente, risparmio_annuo: s.risparmio_annuo, rientro_investimento: s.pbt }} />],
    a && ["energia", "Energia", <div key="n" className="space-y-3"><Monthly data={a} /><Section title="Energia" testid="pratica-info-analisi" data={{ consumo_annuo_kwh: a.consumo_annuo_kwh, produzione_annua_kwh: a.produzione_annua_kwh, resa_specifica_kwh_kwp: a.resa_specifica_kwh_kwp, fasce_kwh: a.fasce_kwh, copertura: s.copertura, potenza_contatore_kw: a.potenza_contatore_kw, prezzo_energia_eur_kwh: a.prezzo_energia_eur_kwh, periodo_bolletta: a.periodo_bolletta, localita_pvgis: a.localita_pvgis }} /></div>],
    (l || docs.includes("layout.png")) && ["tetto", "Tetto", <div key="t" className="space-y-3"><Layout id={d.id} has={docs.includes("layout.png")} /><Falde l={l} /><Section title="Moduli" testid="pratica-info-layout-dati" data={{ modulo: l?.modulo?.modello, potenza_modulo: l?.modulo?.potenza_wp != null ? `${l.modulo.potenza_wp} Wp` : null, moduli_posati: l?.moduli_posati, moduli_max: l?.moduli_max, potenza_max_kwp: l?.potenza_max_kwp, superficie_netta_m2: l?.superficie_netta_m2, tetto: s.roof_type }} /></div>],
    forn && ["fornitura", "Fornitura", <Fornitura key="f" f={forn} />],
    ["dettagli", "Dettagli", <Section key="d" title="Dettagli" testid="pratica-info-partner" data={{ cliente: s.cliente || d.consumer?.name, indirizzo: s.indirizzo, partner: d.installer?.ragione_sociale, p_iva: d.installer?.p_iva, agente: d.agent?.name, email_agente: d.agent?.email, strumento: d.tool_label || d.tool, stato: d.status, creata: d.created_at, aggiornata: d.updated_at }} />],
  ].filter(Boolean);
  return (
    <div className="space-y-4">
      <div className="-mt-2 text-xs text-neutral-400" data-testid="pratica-info-sub">{[s.comune, d.installer?.ragione_sociale].filter(Boolean).join(" · ")}</div>
      <KeyNums d={d} />
      <Trattativa c={{ trattativa: { ...t, fornitore_attuale: undefined } }} testid="pratica-info-steps" />
      <div className="flex flex-wrap gap-2" data-testid="pratica-info-actions">
        {d.tool && <button type="button" className={btn} onClick={onOpen} data-testid="pratica-info-open"><ExternalLink className="h-3.5 w-3.5" />Apri nel configuratore</button>}
        <button type="button" className={btn} onClick={voiceStore.open} data-testid="pratica-info-vesta"><Flame className="h-3.5 w-3.5" />Chiedi a Vesta</button>
        <DocsMenu docs={docs} openDoc={openDoc} sendDoc={sendDoc} d={d} />
      </div>
      <Tabs defaultValue="riepilogo">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`pratica-info-tab-${k}`} className="rounded-full border border-neutral-800 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-neutral-400 data-[state=active]:border-[#C17F59] data-[state=active]:bg-[#C17F59]/15 data-[state=active]:text-[#E8B48F]">{label}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , el]) => <TabsContent key={k} value={k} className="mt-3">{el}</TabsContent>)}
      </Tabs>
    </div>
  );
}

function Body({ kind, d, onClose }) {
  const nav = useNavigate();
  const cli = kind === "cfg" ? (d.summary || {}).cliente || (d.consumer || {}).name : d.cliente;
  const [mail, setMail] = useState(null);
  const docs = d.documenti_disponibili || [];
  const openDoc = async (doc) => { const w = window.open("", "_blank"); const u = await getDoc(d.id, doc); if (u && w) w.location.href = u; else w?.close(); };
  const sendDoc = (doc) => setMail({ doc, draft: { subject: `${doc === "proposta.pdf" ? "Proposta" : "Consulenza"} · ${cli || "consulenza Chiavi in mano"}`, body: `Buongiorno,\n\nin allegato ${doc === "proposta.pdf" ? "la proposta" : "la consulenza"}${cli ? ` per ${cli}` : ""}.\n\nCordiali saluti` } });
  useVestaView(true, { scheda: kind === "cfg" ? "resoconto consulenza Chiavi in mano" : "resoconto calcolo tecnico", config_id: kind === "cfg" ? d.id : undefined, cliente: cli, tipo: d.tool_label || d.tipo, partner: (d.installer || {}).ragione_sociale || d.installer_name });
  const pdf = async () => {
    const w = window.open("", "_blank");
    try { const r = await api.get(`/consulenze-tecniche/${d.id}/pdf`, { responseType: "blob" }); w.location.href = URL.createObjectURL(r.data); }
    catch { w?.close(); toast.error("PDF non disponibile"); }
  };
  const { summary, payload } = d;
  return (
    <div className="space-y-4">
      {mail && <MailComposer draft={mail.draft} onClose={() => setMail(null)} sendUrl={`/rete/configs/${d.id}/email`} sendParams={{ allegati: mail.doc }} attachment={mail.doc === "proposta.pdf" ? "Proposta (PDF)" : "Consulenza (PDF)"} />}
      {kind === "cfg" ? <CfgView d={d} docs={docs} openDoc={openDoc} sendDoc={sendDoc} onOpen={() => { onClose(); nav(`/vesta/configuratore?tool=${d.tool}&config_id=${encodeURIComponent(d.id)}`); }} /> : <>
      <div className="flex flex-wrap gap-2" data-testid="pratica-info-actions">
        {(d.pdf_storage_path || d.pdf_filename) && <button type="button" className={btn} onClick={pdf} data-testid="pratica-info-pdf"><Download className="h-3.5 w-3.5" />Scarica PDF</button>}
        <button type="button" className={btn} onClick={voiceStore.open} data-testid="pratica-info-vesta"><Flame className="h-3.5 w-3.5" />Chiedi a Vesta</button>
      </div>
        <Section title="Cliente e impianto" testid="pratica-info-cliente" data={{ cliente: d.cliente, indirizzo: d.indirizzo, comune: d.comune, potenza_kwp: d.potenza_kwp, partner: d.installer_name }} />
        <Section title="Risultati" testid="pratica-info-risultati" data={summary} />
        <Section title="Parametri inseriti" testid="pratica-info-parametri" data={payload} />
        <Section title="Configurazione" testid="pratica-info-meta" data={{ tipo: d.tipo, creata: d.created_at, da: d.created_by }} />
      </>}
    </div>
  );
}

// Pulsante «i» con resoconto completo di una configurazione (kind: cfg commerciale, ct calcolo tecnico)
export function PraticaInfo({ kind, id, testid }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setD(null); setErr("");
    api.get(kind === "cfg" ? `/rete/configs/${id}` : `/consulenze-tecniche/${id}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Resoconto non disponibile"));
  }, [open, kind, id]);
  const title = d ? (kind === "cfg" ? (d.summary || {}).cliente || (d.consumer || {}).name || "Configurazione" : d.cliente || d.tipo) : "Resoconto";
  return (
    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="contents">
      <button type="button" onClick={() => setOpen(true)} title="Resoconto completo" data-testid={testid}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-neutral-700 text-neutral-300 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]"><Info className="h-3.5 w-3.5" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="pratica-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#C17F59]" data-testid="pratica-info-title">{title}</DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-widest text-neutral-500">{kind === "cfg" ? "Resoconto completo della configurazione" : "Resoconto completo del calcolo tecnico"}</DialogDescription>
          </DialogHeader>
          {err ? <p className="text-sm text-red-400" data-testid="pratica-info-error">{err}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin text-[#C17F59]" /> : <Body kind={kind} d={d} onClose={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </span>
  );
}
